import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { SponsorStudiesStorage } from './sponsor-studies-storage';
import { SponsorStudy } from './sponsor-study';
import { combineLatest, merge, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { first, sortBy, chain } from 'lodash';
import { FormControl } from '@angular/forms';
import { UrlBuilder } from '../core';
import { SponsorUnreadReportsStorage } from '../sponsor/sponsor-unread-reports-storage';
import { SponsorDocumentsCounter } from '../documents/models/documents-counter';
import { CardItem, StudyCard } from './study-card';
import { CategoryNaturalKey } from '../documents';
import { containsCaseInsensitive } from '../core/search-functions';
import { UserPermissionsStorage } from '../user-permissions/user-permissions-storage';
import { UserStudyPermissions, UserPermissions } from '../user-permissions/user-permissions';
import { UserPreferencesStorage } from '../user-preferences/user-preferences-storage';
import { SponsorEQueriesCountersStorage } from '../sponsor/e-queries/sponsor-e-queries-counters-storage';
import { EQueryCounter } from '../e-queries/models/e-query-counter';
import { unresolvedEQueryStatuses } from '../e-queries/models/e-query-status';

@Component({
    selector: 'study-list',
    template: require('./sponsor-study-list.component.html'),
    styles: [require('./sponsor-study-list.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SponsorStudyListComponent implements OnDestroy {
    public searchTerm = new FormControl('');
    public loadingComplete = false;
    public hasAccessToStudies = this.sponsorStudiesStorage.loadStudies().pipe(map(studies => studies.length > 0));

    public get favStudies() {
        return this.filterStudies(true);
    }

    public get otherStudies() {
        return this.filterStudies(false);
    }

    public allStudyCards: StudyCard[] = [];

    public studySorterMenuItems: SorterMenuItem[] = [
        {
            title: 'Protocol',
            getter: (item: StudyCard) => item.study.protocolName.toLowerCase()
        },
        {
            title: 'Number of Subjects',
            getter: (item: StudyCard) => - item.study.numberOfSubjects
        }
    ];

    public get selectedSorter() {
        return this.sorter;
    }

    public set selectedSorter(value: SorterMenuItem) {
        if (!value) {
            return;
        }

        this.sorter = value;
        this.userPreferencesStorage.set(this.sortingMethodStorageKey, this.sorter.title);
    }

    private readonly sortingMethodStorageKey = 'SponsorDashboard_StudiesList_Sorting_Method';
    private sorter: SorterMenuItem;
    private subscription: Subscription;
    private readonly categoryDisplayOrder: CategoryNaturalKey[] = ['result-alerts', 'sponsor-communications'];
    private readonly categoryNamesDisplayMap: { [k in CategoryNaturalKey]?: string } = {
        'result-alerts': 'Result Alerts',
        'sponsor-communications': 'Communications'
    };
    private readonly eQueriesTooltip = unresolvedEQueryStatuses.join(', ');

    constructor(
        private readonly sponsorStudiesStorage: SponsorStudiesStorage,
        private readonly unreadReportsRegistry: SponsorUnreadReportsStorage,
        private readonly urlBuilder: UrlBuilder,
        private readonly userPermissionsStorage: UserPermissionsStorage,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly userPreferencesStorage: UserPreferencesStorage,
        sponsorEQueriesCountersStorage: SponsorEQueriesCountersStorage,
    ) {
        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );

        const unreadCounters$ = this.unreadReportsRegistry.getFreshUnreadsStream();
        const unreadAlertsBySites$ = this.unreadReportsRegistry.getAlertsUnreadBySitesCounter();
        const permissions$ = this.userPermissionsStorage.userStudiesPermissions;
        const sortingMethod$ = this.userPreferencesStorage.get(this.sortingMethodStorageKey);
        const unresolvedEQueries$ = sponsorEQueriesCountersStorage.refreshAllCounters();

        this.subscription = combineLatest([
            sponsorStudiesStorage.loadStudies(true),
            searchTermFlow,
            unreadCounters$,
            unreadAlertsBySites$,
            permissions$,
            sortingMethod$,
            unresolvedEQueries$,
        ]).pipe(
                map(([studies, term, counters, alertCounters, permissions, sortingMethod, unresolvedEQueries]: [SponsorStudy[], string, SponsorDocumentsCounter[], SponsorDocumentsCounter[], UserStudyPermissions, string, EQueryCounter[]]) =>
                    [this.createStudyCards(studies, counters, alertCounters, permissions, unresolvedEQueries), term, sortingMethod]
                ),
                map(([studyCards, term, sortingMethod]: [StudyCard[], string, string]) =>
                    [this.applyQuickSearchFilter(studyCards, term), sortingMethod]
                ))
            .subscribe(([studyCards, sortingMethod]: [StudyCard[], string]) => {
                this.allStudyCards = studyCards;
                this.restoreStudySorter(sortingMethod);
                this.applySorter();
                this.loadingComplete = true;
                this.changeDetectorRef.markForCheck();
            });
    }

    public sortStudies(item: SorterMenuItem): void {
        this.selectedSorter = item;
        this.applySorter();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private applySorter(): void {
        this.allStudyCards = sortBy(this.allStudyCards, this.selectedSorter.getter);
    }

    private filterStudies(onlyFavorites: boolean): StudyCard[] {
        return this.allStudyCards.filter(s => s.study.isFavorite === onlyFavorites);
    }

    private applyQuickSearchFilter(studyCards: StudyCard[], term: string) {
        return term ? studyCards.filter(card =>
            containsCaseInsensitive(card.study.protocolName.toString(), term))
            : studyCards;
    }

    private createStudyCards(studies: SponsorStudy[], counters: SponsorDocumentsCounter[], alertCounters: SponsorDocumentsCounter[], permissions: UserStudyPermissions, eQueryCounters: EQueryCounter[]): StudyCard[] {
        return studies.map(study => {
            const studyCounters: SponsorDocumentsCounter[] = counters.filter(counter => counter.protocolId === study.protocolId);
            const studyAlertCounter: SponsorDocumentsCounter = alertCounters.find(counter => counter.protocolId === study.protocolId);
            const studyPermissions: UserPermissions = permissions[study.protocolId] || new UserPermissions([]);

            const sponsorItems: CardItem[] = this.categoryDisplayOrder
                .filter(key => studyPermissions.canViewCategory(key))
                .map(key => {
                    const counter = studyCounters.find(c => c.rootCategoryId === key);
                    const isDisabled = !(counter && counter.totalDocs > 0);
                    const unreads = counter && counter.totalUnread;
                    return {
                        name: this.categoryNamesDisplayMap[key],
                        counter: unreads || 0,
                        link: this.getUrlByCategory(study, key),
                        categoryId: key,
                        isDisabled: isDisabled
                    };
                });

            const studyItems: CardItem[] = [
                {
                    name: 'Study Sites',
                    counter: study.numberOfSites,
                    isDisabled: study.numberOfSites === 0,
                    link: this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .sites()
                        .url()
                },
                {
                    name: 'Study Subjects',
                    counter: study.numberOfSubjects,
                    isDisabled: study.numberOfSubjects === 0,
                    link: this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(study)
                        .subjects()
                        .url()
                }
            ];

            if (studyPermissions.canViewActivityReport('result-alerts')) {
                studyItems.push(
                    {
                        name: 'Alerts Unread by Sites',
                        counter: studyAlertCounter.totalUnread,
                        isDisabled: studyAlertCounter.totalUnread === 0,
                        queryParams: { unreadOnly: true },
                        link: this.urlBuilder
                            .esa()
                            .sponsor()
                            .study(study)
                            .activityReports()
                            .documents('result-alerts')
                            .url()
                    }
                );
            }

            if (studyPermissions.has('EQueryViewAndRespond')) {
                const eQueryCounter = this.getEQueriesCounter(eQueryCounters, study);

                studyItems.push(
                    {
                        name: 'Unresolved eQueries',
                        isDisabled: !eQueryCounter,
                        counter: eQueryCounter || 0,
                        link: this.urlBuilder
                            .esa()
                            .sponsor()
                            .study(study)
                            .eQueries()
                            .url(),
                        tooltip: this.eQueriesTooltip,
                    }
                );
            }

            const studyCard: StudyCard = {
                study: study,
                sponsorItems: sponsorItems,
                studyItems: studyItems
            };

            return studyCard;
        });
    }

    private getUrlByCategory(study: SponsorStudy, category: CategoryNaturalKey): any[] {
        const studyLink = this.urlBuilder
            .esa()
            .sponsor()
            .study(study);

        switch (category) {
            case 'sponsor-communications':
                return studyLink
                    .sponsorCommunications()
                    .url();

            case 'result-alerts':
                return studyLink
                    .resultAlerts()
                    .url();

            default:
                return this.urlBuilder
                    .esa()
                    .sponsor()
                    .url();
        }
    }

    private restoreStudySorter(sortingMethod: string) {
        this.sorter = this.studySorterMenuItems.find(c => c.title === sortingMethod) || first(this.studySorterMenuItems);
    }

    private getEQueriesCounter(eQueryCounters: EQueryCounter[], study: SponsorStudy): number | null {
        if (!eQueryCounters || eQueryCounters.length === 0) {
            return null;
        }

        const totalUnresolved = chain(eQueryCounters)
            .filter(c => c.protocolId === study.protocolId)
            .sumBy(c => c.totalUnresolved.size)
            .value();

        return totalUnresolved;
    }
}

export interface SorterMenuItem {
    title: string;
    getter: (item: StudyCard) => string | number;
}
